import React from 'react';

import { Box, Button, Grid } from '@mui/material';
import { DateTime } from 'luxon';

import { DeepPartial } from '@hrlogics/helper-types';
import { Field, Form, FormApi, FormSpy } from '@hrlogics/react-components/form';

import { DateFormatPatterns, SSN_MASK } from '@clearverify/constants';

import {
  InboxItemDocument,
  InboxItemsScrollableListDocument,
  useCreateVerificationRequestMutation,
  VerificationRequestsReportListDocument,
} from 'src/api';
import { DateField, MaskedTextField, TextField } from 'src/components/finalForm';
import {
  VerificationRequestCreateByVerifierModalFormConsumerErrorBox,
  VerificationRequestCreateByVerifierModalFormErrorBox,
  VerificationRequestCreateFormPermissiblePurposeField,
  VerificationRequestCreateFormVerificationReportTypeBasicField,
  VerificationRequestCreateModalFormEmployerField,
} from 'src/components/verificationRequest';
import {
  governmentVerificationRequestTypeOptions,
  VerificationRequestCreateFormFields,
} from 'src/constants/verificationRequest';
import { useNotification } from 'src/hooks/app';
import { useVerificationRequestFormData } from 'src/hooks/verificationRequest';
import { useCurrentUserVerifier } from 'src/hooks/verifier';
import { convertEmployerListItemsToEmployerDataItems } from 'src/utils/employer';
import { validateWithYupSchema } from 'src/utils/yup';

import { VerificationRequestCreateFormValues } from './types';
import { verificationRequestCreateValidationSchema } from './utils';

interface VerificationRequestCreateByGovernmentVerifierModalFormProps {
  closeModal: () => void;
}

export const VerificationRequestCreateByGovernmentVerifierModalForm: React.FC<
  VerificationRequestCreateByGovernmentVerifierModalFormProps
> = ({ closeModal }) => {
  const { createApolloContext } = useNotification();

  const { verifierId } = useCurrentUserVerifier();

  const { isFormInfoLoading, resetData, startSubmitting, stopSubmitting } =
    useVerificationRequestFormData();

  React.useEffect(() => {
    return () => resetData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [createVerificationRequest] = useCreateVerificationRequestMutation({
    context: createApolloContext({
      errorMessage: 'Error creating verification request',
      successMessage: 'Verification request successfully created',
    }),
    refetchQueries: [
      InboxItemsScrollableListDocument,
      InboxItemDocument,
      VerificationRequestsReportListDocument,
    ],
  });

  const minDate = React.useMemo(() => DateTime.fromISO('1800-01-01'), []);
  const maxDate = React.useRef(DateTime.now());

  const onSubmit = React.useCallback(
    (
      formValues: VerificationRequestCreateFormValues,
      formApi: FormApi<
        VerificationRequestCreateFormValues,
        DeepPartial<VerificationRequestCreateFormValues>
      >,
    ) => {
      formApi.change(VerificationRequestCreateFormFields.error, null);

      const employeeDateOfBirth = DateTime.fromISO(formValues.employeeDateOfBirth);
      if (!employeeDateOfBirth.isValid) return;

      startSubmitting();

      createVerificationRequest({
        variables: {
          ssn: formValues.ssn,
          employeeFirstName: formValues.employeeFirstName,
          employeeLastName: formValues.employeeLastName,
          employeeBirthDate: employeeDateOfBirth.toISODate(),
          requestType: formValues.verificationReportType,
          permissiblePurposeId: formValues.permissiblePurposeId,
          noteToEmployee: formValues.noteToEmployee,
          companies: convertEmployerListItemsToEmployerDataItems(formValues.employers),
        },
      })
        .finally(stopSubmitting)
        .catch(() => undefined);

      closeModal();
    },
    [closeModal, createVerificationRequest, startSubmitting, stopSubmitting],
  );

  return (
    <Form<VerificationRequestCreateFormValues>
      initialValues={initialValues}
      onSubmit={onSubmit}
      subscription={{ valid: true, submitting: true }}
      validate={validateWithYupSchema(verificationRequestCreateValidationSchema)}
    >
      {({ submitting, valid }) => {
        return (
          <React.Fragment>
            <Grid container spacing={2.5}>
              <Grid size={12}>
                <Field
                  name={VerificationRequestCreateFormFields.ssn}
                  component={MaskedTextField}
                  label="SSN"
                  mask={SSN_MASK}
                  fullWidth
                  required
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <Field
                  name={VerificationRequestCreateFormFields.employeeFirstName}
                  component={TextField}
                  label="Employee First Name"
                  fullWidth
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <Field
                  name={VerificationRequestCreateFormFields.employeeLastName}
                  component={TextField}
                  label="Employee Last Name"
                  fullWidth
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <Field
                  name={VerificationRequestCreateFormFields.employeeDateOfBirth}
                  component={DateField}
                  minDate={minDate}
                  maxDate={maxDate.current}
                  inputFormat={DateFormatPatterns.shortDateWithSlash}
                  defaultValue={DateFormatPatterns.shortDateWithSlash}
                  label="Employee Date of Birth"
                  required
                  fullWidth
                />
              </Grid>

              <Grid size={12}>
                <FormSpy subscription={{ values: true, errors: true }}>
                  {() => <VerificationRequestCreateModalFormEmployerField />}
                </FormSpy>
              </Grid>

              <Grid size={12}>
                <VerificationRequestCreateFormVerificationReportTypeBasicField
                  options={governmentVerificationRequestTypeOptions}
                />
              </Grid>

              <Grid size={12}>
                <VerificationRequestCreateFormPermissiblePurposeField
                  verifierId={verifierId}
                  disabled={submitting}
                />
              </Grid>

              <Grid size={12}>
                <Field
                  name={VerificationRequestCreateFormFields.noteToEmployee}
                  component={TextField}
                  label="Note to Employee (optional)"
                  fullWidth
                />
              </Grid>
            </Grid>

            <VerificationRequestCreateByVerifierModalFormErrorBox />

            <FormSpy subscription={{ values: true, errors: true }}>
              {() => <VerificationRequestCreateByVerifierModalFormConsumerErrorBox />}
            </FormSpy>

            <Box display="flex" gap={2} mt={4} justifyContent="center">
              <Button
                variant="outlined"
                color="error"
                onClick={() => closeModal()}
                disabled={submitting}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={!valid || submitting}
                loading={submitting || isFormInfoLoading}
              >
                Submit request
              </Button>
            </Box>
          </React.Fragment>
        );
      }}
    </Form>
  );
};

const initialValues: Partial<VerificationRequestCreateFormValues> = {
  [VerificationRequestCreateFormFields.verificationReportType]:
    governmentVerificationRequestTypeOptions.at(0)?.value,
  [VerificationRequestCreateFormFields.employers]: [],
  [VerificationRequestCreateFormFields.error]: null,
};
